/**
 * The one chip.
 *
 * A chip is the same object everywhere in the task editor: an evaluation icon, a hairline divider,
 * and a label, on a success/error/neutral ground. It appears in the feedback panel, on the test
 * bar, on a collapsed card, in the trigger picker and in Flow — and before this file it was written
 * out 47 times, so "the same object" was an intention rather than a fact.
 *
 * What the copies had drifted on, all of it found by measuring rather than by reading:
 *
 *   - **Weight.** 22 hand-rolled copies kept `font-medium` after the shared trigger class dropped
 *     it, so the same value rendered heavier in one panel than in the next.
 *   - **Padding.** `px-2 py-1` in most, `px-2.5 py-0.5` in two, `py-1.5` in one.
 *   - **Width caps.** `max-w-24`, `max-w-40` and `max-w-48` all appeared in the SAME feedback
 *     panel, so three chips describing three answers to one question truncated at three widths.
 *   - **Icon and tone.** Three separate copies of each — `chipEvalIcon`, `testBarEvalIcon`,
 *     `ChipEvalIcon`; `testBarEvalChipClass`, `chipColorClasses`, and inline ternaries.
 *
 * Everything here is a class BUILDER rather than a wrapper component, deliberately. The 47 sites
 * differ in what they put inside the chip — a slashed preview, a formula, a media icon, a nested
 * variable chip — and several are `<button>`s carrying their own handlers and ARIA. Swapping a
 * className is a change with no structural risk; rewriting 47 pieces of JSX to fit one component's
 * children contract is not, and would buy nothing the builder doesn't.
 */
import { Ban, Check, Minus, X } from 'lucide-react';

import type React from 'react';

/** Every chip's evaluation. `neutral` is the "counts, but neither right nor wrong" ground. */
export type ChipEvaluation = 'correct' | 'incorrect' | 'neutral';

/**
 * Metrics shared by every chip. Tone, width and modifiers are layered on by `chipClass`; nothing
 * outside this file should restate a padding, a radius or a text size.
 *
 * No `font-medium`: a chip sits inline among body text and a heavier weight reads as a bigger chip,
 * which is what made the targeted-feedback chips look oversized next to the default-state ones.
 */
// `ws-chip` carries no styling of its own — it is the marker globals.css uses to tighten a
// variable chip drawn inside a feedback chip. Defined here so "a feedback chip" has one
// definition and the CSS rule cannot fall out of step with it.
const CHIP_BASE = 'ws-chip flex items-center rounded-md text-xs';
/** Padding and inner gap, the two things `compact` tightens. Split out so there is still one
    definition of each: a caller choosing compact is choosing between these, not writing a size. */
const CHIP_PAD = { normal: 'gap-1 px-2 py-1', compact: 'gap-0.5 px-1.5 py-0.5' };

/**
 * How wide a chip may grow before it truncates.
 *
 * One cap for feedback chips, not the three that were here. `max-w-24` was narrow enough to cut a
 * two-digit value with a slash over it, and it sat in the same wrap-flex row as `max-w-48` chips —
 * so of two chips naming two answers to one question, one truncated and one didn't. These rows
 * wrap, so a wider cap costs a line at worst and truncation costs the teacher the answer.
 */
export type ChipWidth = 'auto' | 'capped' | 'full';

const CHIP_WIDTH: Record<ChipWidth, string> = {
  /** Grows to its content. For a chip that is alone on its line, or already inside a capped box. */
  auto: '',
  /** The feedback-panel default: long previews truncate rather than pushing the panel wide. */
  capped: 'max-w-48 overflow-hidden',
  /** Bounded by the parent only — the test bar, whose own container already does the limiting. */
  full: 'max-w-full',
};

/** Background + foreground for an evaluation. The single source; every other copy delegates here. */
export function chipToneClass(evaluation: ChipEvaluation): string {
  return evaluation === 'correct' ? 'bg-ws-success-bg text-ws-success-fg'
    : evaluation === 'incorrect' ? 'bg-ws-error-bg text-ws-error-fg'
    : 'bg-ws-neutral-bg text-ws-neutral-fg';
}

/**
 * The Check/X/Minus a chip pairs with its colour.
 *
 * Not optional and not decorative: colour alone is not a distinction for a colour-blind teacher,
 * which is the whole reason a chip carries an icon at all. `aria-hidden` because the label beside
 * it already carries the meaning in text.
 */
export function ChipEvalIcon({ evaluation }: { evaluation: ChipEvaluation }) {
  return evaluation === 'correct' ? <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />
    : evaluation === 'incorrect' ? <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />
    : <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />;
}

/** The hairline between a chip's icon and its label. `bg-current` so it inherits the chip's tone. */
export function ChipDivider() {
  return <span aria-hidden='true' className='self-stretch w-px shrink-0 bg-current opacity-40' />;
}

export interface ChipOptions {
  /** Default `capped`. See `ChipWidth`. */
  width?: ChipWidth;
  /** Keeps the chip at its natural size inside a flex row instead of letting it be squeezed. */
  shrink0?: boolean;
  /** Allows the chip to shrink below its content width — for a chip that fills its container. */
  grow?: boolean;
  /** One line only. Pair with a capped width, or a long label simply overflows. */
  nowrap?: boolean;
  /**
   * Tighter padding, for a dense list of chips read at a glance rather than operated.
   *
   * Here rather than as a class the caller adds, because this file owns a chip's padding — the
   * note at the top of it is explicit that nothing outside restates one. The text size does NOT
   * change: `text-xs` is already the floor for a label sitting next to an evaluation icon, and
   * shrinking it buys a few pixels at the cost of the thing being readable.
   */
  compact?: boolean;
  /** For a chip standing in for an absent state ("Inactive", "optional"). */
  italic?: boolean;
  /** Dimmed, for a chip that is configured but not currently firing. */
  dimmed?: boolean;
  /**
   * Draws a diagonal line across the WHOLE chip — icon, divider and label together. Pair with
   * `<ChipSlash />` as the chip's last child.
   *
   * The slash used to cover only the label, and that said the wrong thing. A chip reading
   * "✓ | 40" with a line through just the 40 negates the ANSWER — "40 must not be selected".
   * What these chips report is the opposite: 40 is a correct answer that the student did not
   * select. Crossing the whole chip negates the CONDITION rather than the value, which is what
   * "this correct answer was not selected" needs. The words "(not selected)" used to carry the
   * disambiguation and are gone; the slash has to carry it alone now.
   */
  slashed?: boolean;
  /** Escape hatch for genuinely site-specific layout. Not for metrics — those belong above. */
  className?: string;
}

/**
 * A chip's metrics and layout, with NO tone.
 *
 * For the handful of sites that compute their own colour — the trigger buttons, which pick a tone
 * from the selected state, and the test bar's dashed "unreachable" chip, which has no evaluation to
 * take a tone from. Everything else wants `chipClass`.
 */
export function chipBaseClass(opts: ChipOptions = {}): string {
  const { width = 'capped', shrink0, grow, nowrap, italic, dimmed, slashed, compact, className } = opts;
  return [
    CHIP_BASE,
    CHIP_PAD[compact ? 'compact' : 'normal'],
    CHIP_WIDTH[width],
    slashed ? 'relative' : '',
    grow ? 'min-w-0' : shrink0 === false ? '' : 'shrink-0',
    nowrap ? 'whitespace-nowrap' : '',
    italic ? 'italic' : '',
    dimmed ? 'opacity-75' : '',
    className ?? '',
  ].filter(Boolean).join(' ');
}

/** The class string for a chip. `evaluation` may be a tone name or a ready-made tone class pair. */
export function chipClass(evaluation: ChipEvaluation | string, opts: ChipOptions = {}): string {
  const tone = evaluation === 'correct' || evaluation === 'incorrect' || evaluation === 'neutral'
    ? chipToneClass(evaluation)
    : evaluation;
  return `${chipBaseClass(opts)} ${tone}`;
}

/**
 * A chip that is also the button opening a trigger picker.
 *
 * `gap-1.5` rather than the chip's `gap-1` — it carries a chevron as well as an icon, and at
 * `gap-1` the chevron crowded the label. Full width because it sits as the header of a feedback
 * card and lines up with the card's other controls.
 */
export function chipButtonClass(evaluation: ChipEvaluation | string): string {
  return `${chipClass(evaluation, { width: 'auto', grow: true })} w-full gap-1.5 transition-opacity hover:opacity-80 focus-visible:[outline:2px_auto]`;
}

/**
 * A state that can never fire, wherever it is shown.
 *
 * One word and one look, because there is one fact. The feedback panel used to say "Inactive" while
 * the test bar said "Unreachable" for the identical condition, and "Inactive" was the worse of the
 * two: the app already spends Active / Deactivated / Disabled on things a TEACHER switches, so a
 * system-derived "nothing can reach this" was wearing the vocabulary of a manual toggle, inviting a
 * hunt for the switch that turned it off. The internals never had the confusion — the reason keys
 * and the variables behind the label were called `unreachable` all along.
 *
 * No reason text. The panel already carries the diagnosis in the issues list; repeating it on every
 * dead state turns a quiet marker into a paragraph.
 *
 * `Ban` rather than the evaluation icons: this chip has no evaluation to report, which is the point.
 * Dashed rather than solid, so it reads as an outline of where a chip would be.
 */
export function UnreachableChip({ label, width = 'auto' }: { label: string; width?: ChipWidth }) {
  return (
    // `ws-chip-outlined` because this is the one chip with a real BORDER, and a border adds 2px
    // that every borderless chip beside it does not have — so an "Unreachable" pill stood 26px tall
    // in a row of 24px ones. The class pays for those 2px out of the padding (see globals.css),
    // keeping the dashed outline that makes this read as inactive in the first place.
    <span className={chipClass('border border-dashed border-ws-border text-ws-muted', { width, italic: true, shrink0: false, className: 'ws-chip-outlined' })}>
      <Ban aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />
      <ChipDivider />
      <span className='min-w-0 break-words'>{label}</span>
    </span>
  );
}

/**
 * The diagonal line a `slashed` chip draws across itself.
 *
 * Deliberately not a `text-decoration: line-through`: a horizontal rule over sub/superscripted
 * formula notation read as part of the notation, and earlier feedback was that it looked like the
 * wrong answer was being REQUIRED rather than excluded. A separate overlay in `currentColor` never
 * touches the underlying markup and matches whatever tone the chip is using.
 */
export function ChipSlash() {
  /*
   * An SVG line, not a CSS gradient.
   *
   * This was a `linear-gradient` with hard colour stops, and a hard stop is exactly what a browser
   * does NOT antialias: the colour changes between one pixel and the next, so a diagonal edge
   * staircases. Reported as the slashes looking stepped. A stroked line is antialiased by the
   * renderer, at any angle and any chip size.
   *
   * `preserveAspectRatio='none'` stretches the 0-100 box to whatever the chip is, so the line always
   * runs corner to corner; `vectorEffect='non-scaling-stroke'` keeps the stroke 2 CSS pixels through
   * that stretch, which it otherwise would not be — a wide chip would thin the line and a tall one
   * would fatten it. Two pixels matches the gradient's own ±1px.
   */
  return (
    <svg
      aria-hidden='true'
      className='pointer-events-none absolute inset-0 h-full w-full'
      viewBox='0 0 100 100'
      preserveAspectRatio='none'
    >
      <line x1='0' y1='100' x2='100' y2='0' stroke='currentColor' strokeWidth='2' vectorEffect='non-scaling-stroke' />
    </svg>
  );
}

/**
 * A label with the slash drawn across IT, rather than across the whole chip.
 *
 * `ChipSlash` is `absolute inset-0`, so hung on the chip it ran corner to corner — through the
 * evaluation icon and the divider as well as the words. The slash negates the CONDITION ("Rope
 * was not selected"), not the verdict beside it, and striking the ✓/✗/− made it read as though
 * the evaluation were cancelled too.
 *
 * So the positioning context is the label, and the slash goes inside it. Chips pass their label
 * through here instead of appending a `<ChipSlash />` of their own, which also means a chip can
 * no longer be positioned for a slash it never draws.
 */
export function SlashedLabel({ slashed, className, children }: {
  slashed?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span className={`${slashed ? 'relative ' : ''}${className ?? ''}`}>
      {children}
      {slashed && <ChipSlash />}
    </span>
  );
}

/**
 * A chip's label. Wraps rather than clamping: `line-clamp-*` sets `display:-webkit-box`, which
 * overrides the display of whatever it is applied to — a variable chip inside a clamped label
 * collapsed and drew on top of the text beside it.
 */
export const CHIP_TEXT_CLASS = 'min-w-0 break-words';

/** As `CHIP_TEXT_CLASS`, for a chip that has committed to one line via `nowrap`. */
export const CHIP_TEXT_TRUNCATE_CLASS = 'min-w-0 truncate';
